import React, { useState } from "react";

import ModalContainer from "ui/ModalContainer";

import { WorkbookType } from "../Workbook";
import { useUpdateWorkbookParameterSetsMutation } from "../FillMissingParameterValues/gql/Mutation.update.generated";

import {
  formatQueryTextWithParamValues,
  paramSetFromExisting,
  toParameterSetPayload,
  validateParamSet,
  workbookDetailsRefetch,
} from "../Workbook/util";
import {
  ParameterSetInputGrid,
  useParamSetInputs,
} from "../ParameterSetInputs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationCircle,
  faMagnifyingGlass,
} from "@fortawesome/pro-solid-svg-icons";
import Button from "ui/Button";
import { useParams } from "react-router-dom";
import { QueryTextArea } from "../ReviewQuery";

const EditParameterSetPanel = ({
  onDismiss,
  workbook,
  parameterSetId,
  refetchWorkbook,
}: {
  onDismiss: () => void;
  workbook: WorkbookType;
  parameterSetId: string;
  refetchWorkbook?: boolean;
}) => {
  const { databaseId } = useParams();
  const [queryText, setQueryText] = useState(
    workbook.baselineQuery.queryTextWithAlias,
  );
  const [errorMessage, setErrorMessage] = useState("");
  const currAliasParamMap = workbook.aliasParamMapList.find(
    (ap) => ap.id === parameterSetId,
  );
  const { paramSet, handleParamValueChange, handleParamNullChange } =
    useParamSetInputs(workbook, () =>
      paramSetFromExisting(workbook, currAliasParamMap?.parameters),
    );

  const [updateWorkbookParameterSets] =
    useUpdateWorkbookParameterSetsMutation();

  const handleUpdateParamSet = () => {
    const validateError = validateParamSet(
      paramSet,
      workbook.parameterRefAliases.length,
    );
    if (validateError) {
      setErrorMessage(validateError);
      return;
    }
    const parameterSets = {
      [parameterSetId]: toParameterSetPayload(paramSet),
    };
    const refetchOpts = refetchWorkbook
      ? workbookDetailsRefetch(workbook.id, databaseId)
      : undefined;
    updateWorkbookParameterSets({
      variables: {
        workbookId: workbook.id,
        parameterSets: parameterSets,
      },
      ...refetchOpts,
      onCompleted: () => {
        setErrorMessage("");
        onDismiss();
      },
      onError: (error) => {
        setErrorMessage(error.message);
      },
    });
  };

  const handlePreviewParameters = () => {
    const preview = formatQueryTextWithParamValues(
      workbook.baselineQuery.queryTextWithAlias,
      paramSet,
    );
    setQueryText(preview);
  };

  return (
    <ModalContainer
      title={`Edit Parameter Set${
        refetchWorkbook ? ` (${currAliasParamMap?.name})` : ""
      }`}
      layout="centered"
      onClose={onDismiss}
    >
      <div className="grid gap-4">
        <div className="relative">
          <QueryTextArea
            queryText={queryText}
            className="max-h-[100px] overflow-y-scroll py-4 mb-4"
            showCopyToClipboard
          />
        </div>
        <div>
          <ParameterSetInputGrid
            workbook={workbook}
            paramSet={paramSet}
            onValueChange={handleParamValueChange}
            onNullChange={handleParamNullChange}
            className="mb-2"
          />
          <Button bare onClick={handlePreviewParameters}>
            <FontAwesomeIcon icon={faMagnifyingGlass} /> Preview Parameters
          </Button>
        </div>
        {errorMessage && (
          <div className="text-[#C22426]">
            <FontAwesomeIcon icon={faExclamationCircle} /> {errorMessage}
          </div>
        )}
        <div>
          <button className="btn btn-success" onClick={handleUpdateParamSet}>
            Update Parameter Set
          </button>
        </div>
      </div>
    </ModalContainer>
  );
};

export default EditParameterSetPanel;
